import { useEffect, useState, type ComponentProps } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { EVENT_LIST_ROUTE_PATH, LOGIN_ROUTE_PATH } from '../routes/appRoutes';
import {
  getLoginAttemptInvalidMessage,
  hasLoginTrxId,
  LoginAttemptInvalidError,
  resendLoginCode,
  verifyLoginCode,
} from '../services/authService';

const RESEND_COOLDOWN_SECONDS = 60;

function getVerifyErrorMessage(error: unknown) {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }

  return '인증 처리 중 오류가 발생했습니다.';
}

function LoginVerifyPage() {
  const navigate = useNavigate();
  const [verificationCode, setVerificationCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [invalidDialogMessage, setInvalidDialogMessage] = useState(() =>
    getLoginAttemptInvalidMessage()
  );
  const [noticeMessage, setNoticeMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }

    const timerId = globalThis.setTimeout(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => globalThis.clearTimeout(timerId);
  }, [resendCooldown]);

  const submitVerificationCode = async () => {
    setErrorMessage('');
    setNoticeMessage('');

    const code = verificationCode.trim();

    if (!code) {
      setErrorMessage('인증코드를 입력해주세요.');
      return;
    }

    if (!/^[A-Za-z0-9]{6}$/.test(code)) {
      setErrorMessage('인증코드는 영문과 숫자 6자리로 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      await verifyLoginCode(code);
      navigate(EVENT_LIST_ROUTE_PATH, { replace: true });
    } catch (error) {
      if (error instanceof LoginAttemptInvalidError) {
        setInvalidDialogMessage(error.message);
        return;
      }

      setErrorMessage(getVerifyErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();
    void submitVerificationCode();
  };

  const submitResend = async () => {
    setErrorMessage('');
    setNoticeMessage('');
    setIsResending(true);

    try {
      await resendLoginCode();
      setNoticeMessage('인증코드를 다시 보냈습니다. 이메일을 확인해주세요.');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      if (error instanceof LoginAttemptInvalidError) {
        setInvalidDialogMessage(error.message);
        return;
      }

      setErrorMessage(getVerifyErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  const handleResend = () => {
    void submitResend();
  };

  const closeInvalidDialog = () => {
    setInvalidDialogMessage('');
    navigate(LOGIN_ROUTE_PATH, { replace: true });
  };

  return (
    <main className="auth-page">
      <section className="auth-panel" aria-labelledby="verify-title">
        <div className="auth-heading">
          <h1 id="verify-title">이메일 2차 인증</h1>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <label className="auth-field">
            <span>인증코드</span>
            <input
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              spellCheck={false}
              value={verificationCode}
              onChange={(event) => setVerificationCode(event.target.value)}
              placeholder="영문+숫자 6자리"
              autoComplete="one-time-code"
            />
          </label>

          {errorMessage && <p className="auth-error">{errorMessage}</p>}
          {noticeMessage && <p className="auth-notice">{noticeMessage}</p>}

          <button className="auth-submit" type="submit" disabled={isLoading}>
            {isLoading ? '인증 확인 중...' : '인증 확인'}
          </button>
          <button
            className="auth-secondary-button"
            type="button"
            disabled={isResending || resendCooldown > 0}
            onClick={handleResend}
          >
            {isResending
              ? '재발송 중...'
              : resendCooldown > 0
                ? `인증코드 재발송 (${resendCooldown}초 후 가능)`
                : '인증코드 재발송'}
          </button>
          {!hasLoginTrxId() && (
            <Link className="auth-link" to={LOGIN_ROUTE_PATH}>
              로그인 화면으로 이동
            </Link>
          )}
        </form>
      </section>

      {invalidDialogMessage && (
        <dialog
          className="password-reset-backdrop"
          open
          aria-labelledby="login-attempt-invalid-title"
          onCancel={closeInvalidDialog}
        >
          <section className="password-reset-modal">
            <div className="password-reset-heading">
              <h2 id="login-attempt-invalid-title">인증을 다시 진행해주세요</h2>
              <p>{invalidDialogMessage}</p>
            </div>

            <div className="password-reset-actions">
              <button className="auth-submit password-reset-submit" type="button" onClick={closeInvalidDialog}>
                확인
              </button>
            </div>
          </section>
        </dialog>
      )}
    </main>
  );
}

export default LoginVerifyPage;
